import { useEffect, useState } from 'react';
import LatexHTMLParser from '@/components/ora/Global/LatexHTMLParser';
import { Article, ArticleSection } from '@/types/Article';
import { fetchArticle } from '@/services/library';
import { fetchHelpDocBySlug } from '@/services/helpDocs';
import { HelpDocPage } from '@/types/HelpDocs';
import { getString } from '@/utils/chatUtils/chatDefaultUtils';

type ArticleBlockBodyProps = {
  item?: Record<string, unknown>;
};

const MAX_EXPANDED_ARTICLE_SECTIONS = 60;
const MAX_EXPANDED_ARTICLE_HTML_CHARS = 250_000;

const limitSectionsByHtmlSize = (sections: Article['sections']) => {
  let renderedChars = 0;

  return sections.filter((section) => {
    if (renderedChars >= MAX_EXPANDED_ARTICLE_HTML_CHARS) return false;

    renderedChars += section.text.length;
    return true;
  });
};

export const ArticleBlockBody = ({ item }: ArticleBlockBodyProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [fetchedArticle, setFetchedArticle] = useState<Article | undefined>();
  const [fetchedHelpDoc, setFetchedHelpDoc] = useState<HelpDocPage | null>();
  const [isLoadingArticle, setIsLoadingArticle] = useState(false);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
  const article = (item?.article as Article | undefined) || fetchedArticle;
  const articleIdOrSlug = getString(item?.articleIdOrSlug);
  const helpDocSlug = getString(item?.helpDocSlug);

  const helpDocSections: ArticleSection[] = fetchedHelpDoc?.html
    ? [
        {
          id: fetchedHelpDoc.slug,
          ord: 0,
          text: fetchedHelpDoc.html,
          references: [],
          isQAed: true,
        },
      ]
    : [];

  const sections = article?.sections || helpDocSections;

  const visibleSections = isExpanded
    ? limitSectionsByHtmlSize(sections).slice(0, MAX_EXPANDED_ARTICLE_SECTIONS)
    : sections.slice(0, 1);

  useEffect(() => {
    if (helpDocSlug || !articleIdOrSlug || article) return;

    let cancelled = false;
    setIsLoadingArticle(true);

    void fetchArticle(articleIdOrSlug)
      .then((nextArticle) => {
        if (!cancelled) setFetchedArticle(nextArticle);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingArticle(false);
          setHasAttemptedFetch(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [article, articleIdOrSlug, helpDocSlug]);

  useEffect(() => {
    if (!helpDocSlug || fetchedHelpDoc) return;

    let cancelled = false;
    setIsLoadingArticle(true);

    void fetchHelpDocBySlug(helpDocSlug)
      .then((nextHelpDoc) => {
        if (!cancelled) setFetchedHelpDoc(nextHelpDoc);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingArticle(false);
          setHasAttemptedFetch(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fetchedHelpDoc, helpDocSlug]);

  // A citation that can still resolve to a full article/help-doc (has an id/slug
  // and hasn't finished fetching) shows a loading state — never the "not
  // available" message mid-fetch (which regressed when the snippet fallback was
  // removed, e.g. on reloaded conversations where item.loading is persisted false).
  const isResolvable = Boolean(articleIdOrSlug || helpDocSlug);

  const isResolving =
    isResolvable &&
    !sections.length &&
    (isLoadingArticle || !hasAttemptedFetch);

  if (isResolving) {
    return <div className="chat-ui_missing">Loading article...</div>;
  }

  // Only ever render a real, full Ora article (or help doc) — never a retrieved
  // section snippet. If the full article couldn't be resolved, show nothing
  // usable rather than a fragment.
  if (!sections.length) {
    return (
      <div className="chat-ui_missing">
        Article content was not available for this citation.
      </div>
    );
  }

  return (
    <div className="chat-ui_article">
      {/* Always surface the real article title above the (collapsible) body so
          the block reads as the article, not a bare section — the section HTML
          leads with its own heading, which otherwise makes the collapsed
          preview look like a lone section. Inline [A#] citation blocks use a
          generic "Article" wrapper header (the title lives here instead), so
          this never double-renders the title. */}
      {article?.title ? (
        <h3 className="chat-ui_article-title">{article.title}</h3>
      ) : null}
      <div
        className={`chat-ui_article-body ${isExpanded ? 'is-expanded' : ''}`}
      >
        {visibleSections.map((section) => (
          <section className="prose-ora" key={section.id}>
            <LatexHTMLParser html={section.text} />
          </section>
        ))}
      </div>
      {!isExpanded ? (
        <button
          className="chat-ui_article-read-all"
          onClick={() => setIsExpanded(true)}
          type="button"
        >
          Read Full Article
        </button>
      ) : null}
    </div>
  );
};
